// Reporting on checks and damage: the Excel sheets and the printable vehicle condition report.
// Both are built from the same damage register, so "new", "still there" and "repaired" always agree.
import { buildDamageRegister, damageStatusText, isFirstSighting, itemBySighting, severityLabel, viewLabel, normalizeSeverity, type DamageItem, type DamageSighting } from "@/lib/damageRegister";
import { localIsoDay } from "@/lib/vehicleChecks";
import { photoUrl, type DamageData, type InspectionRow } from "@/lib/damageData";

export type SheetRow = Record<string, string | number>;

export interface ExportSheets {
  damageItems: SheetRow[];
  damageLog: SheetRow[];
  inspections: SheetRow[];
}

export interface ReportOptions {
  /** Only this date range (YYYY-MM-DD, inclusive) -- applied to inspections, never to working out what is "new". */
  dateFrom?: string | null;
  dateTo?: string | null;
  /** YYYY-MM-DD or an ISO timestamp -> the app's display date (e.g. 03/11/2026). */
  formatDay: (iso: string) => string;
  timeZone?: string;
}

const inRange = (day: string, from?: string | null, to?: string | null) => (!from || day >= from) && (!to || day <= to);

const trendText = (item: DamageItem): string =>
  item.severityTrend === "worse" ? "Got worse" : item.severityTrend === "better" ? "Improved" : "No change";

const inspectionKind = (t: string | null): string => (t === "admin" ? "Admin" : "Driver");

/** One register per vehicle (damage is tracked per vehicle). */
function registersByVehicle(data: DamageData, timeZone?: string): Map<string, DamageItem[]> {
  const vehicleOf = new Map(data.inspections.map((i) => [i.id, i.vehicle_number]));
  const byVehicle = new Map<string, DamageSighting[]>();
  for (const s of data.sightings) {
    const v = vehicleOf.get(s.inspection_id);
    if (!v) continue;
    (byVehicle.get(v) ?? byVehicle.set(v, []).get(v)!).push(s);
  }
  const out = new Map<string, DamageItem[]>();
  for (const [v, list] of byVehicle) out.set(v, buildDamageRegister(list, timeZone));
  return out;
}

const byFleetNumber = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

export function buildExportSheets(data: DamageData, opts: ReportOptions): ExportSheets {
  const { dateFrom, dateTo, formatDay, timeZone } = opts;
  const dayOf = (ts: string) => localIsoDay(ts, timeZone);
  const registers = registersByVehicle(data, timeZone);
  const vehicleOf = new Map(data.inspections.map((i) => [i.id, i.vehicle_number]));

  const damageItems: SheetRow[] = [];
  const damageLog: SheetRow[] = [];

  for (const vehicle of [...registers.keys()].sort(byFleetNumber)) {
    const items = registers.get(vehicle)!;
    for (const item of items) {
      const seenInRange = item.sightings.some((s) => inRange(dayOf(s.inspection_at), dateFrom, dateTo));
      const repairedInRange = !!item.repairedOn && inRange(item.repairedOn, dateFrom, dateTo);
      if (!seenInRange && !repairedInRange) continue;
      damageItems.push({
        Vehicle: vehicle,
        Side: viewLabel(item.view),
        "Damage type": item.damageType,
        "First seen": formatDay(item.firstSeenAt),
        "Last seen": formatDay(item.lastSeenAt),
        "Times seen": item.sightings.length,
        "First severity": severityLabel(item.firstSeverity),
        "Latest severity": severityLabel(item.latestSeverity),
        "Severity change": trendText(item),
        Status: damageStatusText(item, formatDay),
        "Repaired on": item.repairedOn ? formatDay(item.repairedOn) : "",
        "Repaired by": item.repairedBy ?? "",
        Notes: item.notes.join(" | "),
      });
    }

    const lookup = itemBySighting(items);
    const sorted = [...items.flatMap((i) => i.sightings)].sort((a, b) => new Date(a.inspection_at).getTime() - new Date(b.inspection_at).getTime());
    for (const s of sorted) {
      if (!inRange(dayOf(s.inspection_at), dateFrom, dateTo)) continue;
      const item = lookup.get(s.id)!;
      damageLog.push({
        Vehicle: vehicle,
        "Check date": formatDay(s.inspection_at),
        "Check type": inspectionKind(s.inspection_type),
        "Checked by": s.inspected_by,
        Side: viewLabel(s.photo_type),
        "Damage type": s.damage_type,
        Severity: severityLabel(s.severity),
        Notes: s.notes ?? "",
        "New or seen before": isFirstSighting(item, s) ? "New" : "Seen before",
        "Status now": damageStatusText(item, formatDay),
        "Photo link": photoUrl(s.storage_path),
      });
    }
  }

  const marksByInspection = new Map<string, DamageSighting[]>();
  for (const s of data.sightings) (marksByInspection.get(s.inspection_id) ?? marksByInspection.set(s.inspection_id, []).get(s.inspection_id)!).push(s);
  const newByInspection = new Map<string, number>();
  for (const items of registers.values()) {
    for (const item of items) newByInspection.set(item.sightings[0].inspection_id, (newByInspection.get(item.sightings[0].inspection_id) ?? 0) + 1);
  }
  const issuesByInspection = new Map<string, string[]>();
  for (const i of data.issues) {
    (issuesByInspection.get(i.inspection_id) ?? issuesByInspection.set(i.inspection_id, []).get(i.inspection_id)!).push(
      `${i.label} (${i.status}${i.note ? `: ${i.note}` : ""})`
    );
  }

  const inspections: SheetRow[] = data.inspections
    .filter((i) => inRange(dayOf(i.created_at), dateFrom, dateTo))
    .sort((a, b) => byFleetNumber(a.vehicle_number, b.vehicle_number) || new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .map((i) => {
      const issues = issuesByInspection.get(i.id) ?? [];
      return {
        Vehicle: i.vehicle_number,
        "Check date": formatDay(i.created_at),
        "Check type": inspectionKind(i.inspection_type),
        "Checked by": i.driver_name ?? "",
        "Overall status": i.overall_status ?? "",
        Completed: i.completed_at ? "Yes" : "No",
        "% done": i.completed_at ? 100 : i.progress_pct ?? "",
        "Odometer (km)": i.odometer_km ?? "",
        "Damage marks": marksByInspection.get(i.id)?.length ?? 0,
        "New damage found": newByInspection.get(i.id) ?? 0,
        "Items needing attention": issues.length,
        "Attention details": issues.join(" | "),
        "General notes": i.notes ?? "",
      };
    });

  return { damageItems, damageLog, inspections };
}

// ---------------------------------------------------------------------------
// Printable condition report
// ---------------------------------------------------------------------------

export const escapeHtml = (v: unknown): string =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export interface ReportVehicle {
  vehicle_number: string;
  registration?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
}

const SEVERITY_COLOUR: Record<string, string> = { minor: "#d97706", moderate: "#ea580c", severe: "#dc2626", unknown: "#6b7280" };

const PHOTO_ORDER = ["exterior_front", "exterior_rear", "exterior_driver", "exterior_passenger", "oil_dipstick", "odometer"];

export function buildConditionReportHtml(args: {
  vehicle: ReportVehicle;
  data: DamageData;
  companyName: string;
  generatedOn: string;
  formatDay: (iso: string) => string;
  formatDateTime: (iso: string) => string;
  timeZone?: string;
}): string {
  const { vehicle, data, companyName, generatedOn, formatDay, formatDateTime, timeZone } = args;
  const register = buildDamageRegister(data.sightings, timeZone);
  const lookup = itemBySighting(register);

  const present = register.filter((i) => i.status === "present");
  const repaired = register.filter((i) => i.status === "repaired");
  const cameBack = register.filter((i) => i.reappeared);
  const admins = data.inspections.filter((i) => i.inspection_type === "admin").length;

  const e = escapeHtml;
  const title = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ");

  const registerRows = register
    .map((item, idx) => {
      const sev = item.firstSeverity === item.latestSeverity ? severityLabel(item.latestSeverity) : `${severityLabel(item.firstSeverity)} → ${severityLabel(item.latestSeverity)}`;
      return `<tr>
        <td>${idx + 1}</td><td>${e(viewLabel(item.view))}</td><td>${e(item.damageType)}</td>
        <td>${e(formatDay(item.firstSeenAt))}</td><td>${e(formatDay(item.lastSeenAt))}</td><td>${item.sightings.length}</td>
        <td>${e(sev)}</td><td>${e(damageStatusText(item, formatDay))}</td><td>${e(item.notes.join(" | "))}</td></tr>`;
    })
    .join("");

  const photosByInspection = new Map<string, typeof data.photos>();
  for (const p of data.photos) (photosByInspection.get(p.inspection_id) ?? photosByInspection.set(p.inspection_id, []).get(p.inspection_id)!).push(p);
  const issuesByInspection = new Map<string, typeof data.issues>();
  for (const i of data.issues) (issuesByInspection.get(i.inspection_id) ?? issuesByInspection.set(i.inspection_id, []).get(i.inspection_id)!).push(i);

  const checkBlock = (insp: InspectionRow): string => {
    const photos = [...(photosByInspection.get(insp.id) ?? [])].sort((a, b) => PHOTO_ORDER.indexOf(a.photo_type) - PHOTO_ORDER.indexOf(b.photo_type));
    const marksByPhoto = new Map<string, DamageSighting[]>();
    for (const s of data.sightings.filter((x) => x.inspection_id === insp.id)) {
      (marksByPhoto.get(s.photo_id) ?? marksByPhoto.set(s.photo_id, []).get(s.photo_id)!).push(s);
    }

    const legend: string[] = [];
    const photoHtml = photos
      .map((p) => {
        const marks = marksByPhoto.get(p.id) ?? [];
        const dots = marks
          .map((m, i) => {
            const colour = SEVERITY_COLOUR[normalizeSeverity(m.severity)];
            const item = lookup.get(m.id)!;
            const status = isFirstSighting(item, m) ? "NEW" : `Seen before (first seen ${formatDay(item.firstSeenAt)})`;
            legend.push(
              `<tr><td><b>${e(viewLabel(p.photo_type))} ${i + 1}</b></td><td>${e(m.damage_type)}</td><td>${e(severityLabel(m.severity))}</td><td>${e(status)}</td><td>${e(damageStatusText(item, formatDay))}</td><td>${e(m.notes ?? "")}</td></tr>`
            );
            return `<span class="dot" style="left:${Number(m.x_percent)}%;top:${Number(m.y_percent)}%;background:${colour}">${i + 1}</span>`;
          })
          .join("");
        return `<figure><div class="ph"><img src="${e(photoUrl(p.storage_path))}" alt="${e(viewLabel(p.photo_type))}">${dots}</div><figcaption>${e(viewLabel(p.photo_type))}</figcaption></figure>`;
      })
      .join("");

    const issues = issuesByInspection.get(insp.id) ?? [];
    const done = insp.completed_at ? "Completed" : "Not completed";
    return `<section class="check">
      <h3>${e(formatDateTime(insp.created_at))} &middot; ${e(inspectionKind(insp.inspection_type))} check &middot; ${e(insp.driver_name ?? "")}</h3>
      <p class="meta">${e(done)}${insp.overall_status ? ` &middot; Overall: <b>${e(insp.overall_status)}</b>` : ""}${insp.odometer_km ? ` &middot; Odometer: ${Number(insp.odometer_km).toLocaleString()} km` : ""}</p>
      ${insp.notes ? `<p class="notes"><b>Notes:</b> ${e(insp.notes)}</p>` : ""}
      ${issues.length ? `<p class="meta"><b>Needing attention:</b></p><ul>${issues.map((i) => `<li>${e(i.category)} &ndash; ${e(i.label)}: <b>${e(i.status)}</b>${i.note ? ` &ndash; ${e(i.note)}` : ""}</li>`).join("")}</ul>` : ""}
      ${photoHtml ? `<div class="photos">${photoHtml}</div>` : ""}
      ${legend.length ? `<table class="legend"><thead><tr><th>Mark</th><th>Type</th><th>Severity</th><th>This check</th><th>Status now</th><th>Notes</th></tr></thead><tbody>${legend.join("")}</tbody></table>` : ""}
    </section>`;
  };

  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>Vehicle ${e(vehicle.vehicle_number)} condition report</title>
<style>
  *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px;font-size:12px}
  h1{font-size:20px;margin:0 0 2px} h2{font-size:15px;margin:22px 0 8px;border-bottom:2px solid #111;padding-bottom:3px} h3{font-size:13px;margin:0 0 4px}
  .sub{color:#555;margin:0 0 12px} .stats{display:flex;gap:10px;flex-wrap:wrap;margin:10px 0}
  .stat{border:1px solid #ccc;border-radius:6px;padding:8px 12px;min-width:110px} .stat b{display:block;font-size:18px}
  table{border-collapse:collapse;width:100%} th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;vertical-align:top} th{background:#f2f2f2}
  .check{border-top:1px solid #bbb;padding-top:10px;margin-top:14px} .meta{margin:2px 0;color:#333} .notes{margin:4px 0}
  .photos{display:flex;gap:8px;flex-wrap:wrap;margin:8px 0} figure{margin:0;width:23%;min-width:150px;break-inside:avoid}
  .ph{position:relative} .ph img{width:100%;display:block;border:1px solid #999} figcaption{font-size:11px;color:#444;text-align:center}
  .dot{position:absolute;width:18px;height:18px;margin:-9px 0 0 -9px;border-radius:50%;border:2px solid #fff;color:#fff;font-size:10px;font-weight:bold;display:flex;align-items:center;justify-content:center}
  .legend{margin-top:6px;font-size:11px} .none{color:#555}
  .noprint{margin:0 0 14px} .noprint button{font-size:13px;padding:6px 14px;cursor:pointer}
  @media print{body{margin:10mm} .noprint{display:none} .check{break-inside:auto} figure,tr{break-inside:avoid} h2{break-after:avoid}}
</style></head><body>
<p class="noprint"><button onclick="window.print()">Print / Save as PDF</button></p>
<h1>${e(companyName)} &ndash; Vehicle condition report</h1>
<p class="sub">Vehicle <b>${e(vehicle.vehicle_number)}</b>${vehicle.registration ? ` &middot; ${e(vehicle.registration)}` : ""}${title ? ` &middot; ${e(title)}` : ""} &middot; Generated ${e(generatedOn)}</p>
<div class="stats">
  <div class="stat"><b>${data.inspections.length}</b>checks (${admins} admin)</div>
  <div class="stat"><b>${present.length}</b>damage still there</div>
  <div class="stat"><b>${repaired.length}</b>damage repaired</div>
  <div class="stat"><b>${cameBack.length}</b>came back after repair</div>
</div>
<h2>Damage register</h2>
${register.length ? `<table><thead><tr><th>#</th><th>Side</th><th>Type</th><th>First seen</th><th>Last seen</th><th>Seen</th><th>Severity</th><th>Status</th><th>Notes</th></tr></thead><tbody>${registerRows}</tbody></table>` : `<p class="none">No damage has been marked on this vehicle.</p>`}
<h2>Check history (newest first)</h2>
${data.inspections.length ? data.inspections.map(checkBlock).join("") : `<p class="none">No completed checks on record.</p>`}
</body></html>`;
}
